import { useState, useEffect } from "react";
import useSWR from 'swr';
import axios from "axios";
import TabPageLayout, { DataCard, EmptyState } from "./TabPageLayout";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import StatusBadge from "./StatusBadge";
import FilterChips from "./FilterChips";
import PinModal from "./PinModal";
import { Building2, Search, Edit, Check, User, Lock, Eye, EyeOff, Trash2, AlertTriangle, Database, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import GmbIntegrationCard from "./GmbIntegrationCard";
import { getAdminUserData } from "../utils/superAdmin";
import { API_URL } from "../config/api";

const PLATFORM_API = `${API_URL}/v2/super-admin`;

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const fetcher = async (url) => {
  const token = localStorage.getItem("jwtToken");
  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (response.data.organizations) {
    return response.data.organizations;
  }
  return response.data;
};

// Auth + PIN headers for sensitive actions
const pinHeaders = (pin) => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
    'X-Admin-Pin': pin,
  }
});

const OrganizationsManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [editingOrg, setEditingOrg] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  // Sensitive-action PIN prompt: { label, run(pin) }
  const [pinPrompt, setPinPrompt] = useState(null);
  const requirePin = (label, run) => setPinPrompt({ label, run });

  const debouncedSearch = useDebounce(searchTerm, 300);

  const { data: organizations = [], isLoading, mutate } = useSWR(
    `${PLATFORM_API}/organizations`,
    fetcher,
    { revalidateOnFocus: true, refreshInterval: 30000 }
  );

  const fetchOrganizations = () => mutate();

  const filteredOrgs = organizations
    .filter(org => {
      const matchesSearch =
        org.name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        org.slug?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        org.ownerName?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        org.ownerEmail?.toLowerCase().includes(debouncedSearch.toLowerCase());
      const matchesStatus = filterStatus === "all" || org.status === filterStatus;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let compareA, compareB;
      switch (sortBy) {
        case "name":
          compareA = a.name?.toLowerCase() || "";
          compareB = b.name?.toLowerCase() || "";
          break;
        case "createdAt":
          compareA = new Date(a.createdAt);
          compareB = new Date(b.createdAt);
          break;
        default:
          return 0;
      }
      return sortOrder === "asc" ? (compareA > compareB ? 1 : -1) : (compareA < compareB ? 1 : -1);
    });

  // ---------- Sensitive actions (all PIN-confirmed + audited) ----------

  const handleSuspendToggle = (org) => {
    const suspending = org.status !== 'suspended' && org.status !== 'soft_deleted';
    const verb = suspending ? 'Suspend' : (org.status === 'soft_deleted' ? 'Restore' : 'Activate');
    requirePin(`${verb} "${org.name}"`, async (pin) => {
      const endpoint = suspending
        ? `${PLATFORM_API}/organizations/${org.id}/suspend`
        : `${PLATFORM_API}/organizations/${org.id}/activate`;
      await axios.post(endpoint, {}, pinHeaders(pin));
      toast.success(`${org.name} ${suspending ? 'suspended' : 'activated'}`);
      fetchOrganizations();
    });
  };

  const handleApprove = (org) => {
    requirePin(`Approve and activate "${org.name}"`, async (pin) => {
      await axios.post(`${PLATFORM_API}/organizations/${org.id}/approve`, {}, pinHeaders(pin));
      toast.success(`${org.name} is now approved and active`);
      fetchOrganizations();
    });
  };

  const handleReject = (org) => {
    requirePin(`Reject and remove the pending signup "${org.name}" — this cannot be undone`, async (pin) => {
      await axios.post(`${PLATFORM_API}/organizations/${org.id}/reject`, {}, pinHeaders(pin));
      toast.success(`${org.name} rejected`);
      fetchOrganizations();
    });
  };

  const handleLoginAsOrg = (org) => {
    requirePin(`Log in to "${org.name}" as their admin — 12-hour support session, audited on both sides`, async (pin) => {
      const response = await axios.post(
        `${PLATFORM_API}/organizations/${org.id}/super-admin-login`,
        {},
        pinHeaders(pin)
      );
      if (response.data.success) {
        localStorage.setItem("jwtToken", response.data.token);
        localStorage.setItem("userType", "operator");
        localStorage.setItem("userData", JSON.stringify({
          username: response.data.username,
          name: response.data.name,
          organizationId: org.id,
          supportSession: true,
        }));
        toast.success(`Logged in as ${org.name} (support session)`);
        window.location.href = "/admin";
      }
    });
  };

  const handleEdit = async (org) => {
    setEditingOrg({ ...org });
    setNewAdminPassword("");
    setShowPassword(false);
    try {
      const token = localStorage.getItem("jwtToken");
      const response = await axios.get(
        `${PLATFORM_API}/organizations/${org.id}/admin-user`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAdminUser({ ...response.data, originalUsername: response.data.username });
    } catch (error) {
      setAdminUser({ username: 'admin', originalUsername: 'admin' });
    }
    setShowEditDialog(true);
  };

  const handleSaveEdit = () => {
    requirePin(`Save changes to "${editingOrg.name}"`, async (pin) => {
      await axios.put(
        `${PLATFORM_API}/organizations/${editingOrg.id}`,
        editingOrg,
        pinHeaders(pin)
      );
      const adminUpdates = {};
      if (adminUser && adminUser.username !== adminUser.originalUsername) {
        adminUpdates.username = adminUser.username;
      }
      if (newAdminPassword) {
        adminUpdates.password = newAdminPassword;
      }
      if (Object.keys(adminUpdates).length > 0) {
        await axios.put(
          `${PLATFORM_API}/organizations/${editingOrg.id}/admin-user`,
          adminUpdates,
          pinHeaders(pin)
        );
      }
      toast.success("Organization updated");
      setShowEditDialog(false);
      setNewAdminPassword("");
      fetchOrganizations();
    });
  };

  const handleDeleteOrg = (org) => {
    if (org.status !== 'suspended' && org.status !== 'soft_deleted') {
      toast.error("Organization must be suspended before deletion. Please suspend it first.");
      return;
    }
    const soft = org.status === 'suspended';
    setOrgToDelete({
      org,
      newStatus: soft ? "soft_deleted" : "deleted",
      confirmMessage: soft
        ? `Soft delete "${org.name}"? This will hide the organization but preserve all data. This action can be reversed.`
        : `PERMANENTLY DELETE "${org.name}"? This will CASCADE DELETE all data including team members, assets, trips, and bookings. THIS CANNOT BE UNDONE!`,
    });
    setDeleteConfirmDialog(true);
  };

  const confirmDelete = () => {
    if (orgToDelete?.newStatus === 'deleted' && deleteConfirmText !== 'Delete') {
      toast.error('Please type "Delete" to confirm permanent deletion');
      return;
    }
    const { org, newStatus } = orgToDelete;
    requirePin(
      newStatus === 'deleted' ? `Permanently delete "${org.name}" and ALL its data` : `Soft delete "${org.name}"`,
      async (pin) => {
        const response = await axios.delete(`${PLATFORM_API}/organizations/${org.id}`, pinHeaders(pin));
        if (response.data.stage === "soft_delete") {
          toast.success(`Organization "${org.name}" soft deleted (reversible)`);
        } else if (response.data.stage === "hard_delete") {
          toast.success(`Organization "${org.name}" permanently deleted`);
        }
        setDeleteConfirmDialog(false);
        setOrgToDelete(null);
        setDeleteConfirmText("");
        fetchOrganizations();
      }
    );
  };

  // Stats
  const stats = {
    total: organizations.length,
    active: organizations.filter(o => o.status === 'active').length,
    pending: organizations.filter(o => o.status === 'pending').length,
    trial: organizations.filter(o => o.status === 'trial').length,
  };

  const sortToggle = (field, defaultOrder = 'asc') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder(defaultOrder);
    }
  };

  return (
    <TabPageLayout
      title="Accounts"
      subtitle={`${stats.total} organizations • ${stats.pending} pending approval`}
      loading={isLoading}
      actions={
        <Button variant="outline" size="sm" onClick={() => mutate()} className="h-9" aria-label="Refresh" data-testid="accounts-refresh-button">
          <RefreshCw className="w-4 h-4" />
        </Button>
      }
    >
      <div className="space-y-6">
        {/* No-PIN heads-up (sensitive actions on this tab require a PIN) */}
        {!getAdminUserData().has_pin && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-xl px-4 py-3" data-testid="accounts-no-pin-notice">
            You don't have an action PIN yet — sensitive actions on this tab (activate, suspend, delete, edits, login-as) will be blocked. Set one in Team → Edit → Access.
          </div>
        )}

        {/* Search and Filters */}
        <DataCard>
          <div className="space-y-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-10"
                data-testid="accounts-search-input"
              />
            </div>
            <FilterChips
              options={[
                { value: 'all', label: 'All', count: stats.total },
                { value: 'active', label: 'Active', count: stats.active },
                { value: 'pending', label: 'Pending', count: stats.pending },
                { value: 'trial', label: 'Trial', count: stats.trial },
                { value: 'suspended', label: 'Suspended' },
              ]}
              value={filterStatus}
              onChange={setFilterStatus}
              testIdPrefix="accounts-filter"
            />
          </div>
        </DataCard>

        {/* Organizations table */}
        <DataCard className="overflow-hidden">
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="w-full text-sm min-w-[800px]" data-testid="accounts-table">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => sortToggle('name')}
                  >
                    <span className="inline-flex items-center gap-1">
                      Organization
                      {sortBy === 'name' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                    </span>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Owner</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Contact</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                  <th
                    className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => sortToggle('createdAt', 'desc')}
                  >
                    <span className="inline-flex items-center gap-1">
                      Created
                      {sortBy === 'createdAt' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                    </span>
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredOrgs.map(org => (
                  <tr key={org.id} className="hover:bg-gray-50 transition-colors" data-testid={`accounts-row-${org.slug}`}>
                    <td className="px-4 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{org.name}</div>
                        <div className="text-sm text-gray-500 font-mono">{org.slug}</div>
                        {org.status === 'active' && org.dbName && (
                          <div className="flex items-center gap-1 mt-1">
                            <Database className="h-3 w-3 text-green-600" />
                            <span className="text-xs font-mono text-green-600">{org.dbName}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">{org.ownerName}</div>
                        <div className="text-gray-600">{org.ownerEmail}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">{org.ownerPhone || '—'}</td>
                    <td className="px-4 py-4">
                      <StatusBadge status={org.status} />
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {new Date(org.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(org)}
                          className="w-20"
                          data-testid={`edit-org-${org.slug}`}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        {org.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-20 text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleReject(org)}
                              title="Reject and remove this pending signup"
                              data-testid={`reject-org-${org.slug}`}
                            >
                              Reject
                            </Button>
                            <Button
                              size="sm"
                              className="w-24 bg-blue-600 hover:bg-blue-700 text-white font-normal"
                              onClick={() => handleApprove(org)}
                              data-testid={`approve-org-${org.slug}`}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Activate
                            </Button>
                          </>
                        )}
                        {org.status === 'suspended' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-20 text-red-600 border-red-200 hover:bg-red-50 font-normal"
                              onClick={() => handleDeleteOrg(org)}
                              title="Soft delete organization"
                              data-testid={`delete-org-${org.slug}`}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                            <Button
                              size="sm"
                              className="w-24 bg-blue-600 hover:bg-blue-700 text-white font-normal"
                              onClick={() => handleSuspendToggle(org)}
                            >
                              Activate
                            </Button>
                          </>
                        )}
                        {org.status === 'soft_deleted' && (
                          <>
                            <Button
                              size="sm"
                              className="w-32 bg-red-600 hover:bg-red-700 text-white font-normal"
                              onClick={() => handleDeleteOrg(org)}
                              title="Permanently delete — cannot be undone"
                              data-testid={`delete-forever-org-${org.slug}`}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete Forever
                            </Button>
                            <Button
                              size="sm"
                              className="w-24 bg-blue-600 hover:bg-blue-700 text-white font-normal"
                              onClick={() => handleSuspendToggle(org)}
                            >
                              Restore
                            </Button>
                          </>
                        )}
                        {(org.status === 'active' || org.status === 'trial') && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-24 text-red-600 border-red-200 hover:bg-red-50 font-normal"
                            onClick={() => handleSuspendToggle(org)}
                            data-testid={`suspend-org-${org.slug}`}
                          >
                            Suspend
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DataCard>

        {filteredOrgs.length === 0 && !isLoading && (
          <EmptyState
            icon={<Building2 />}
            title="No organizations found"
            description={debouncedSearch ? `No results for "${debouncedSearch}"` : "Organizations will appear here once they sign up."}
          />
        )}

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="text-xl">Edit Organization</DialogTitle>
            </DialogHeader>
            {editingOrg && (
              <div className="space-y-6 max-h-[70vh] overflow-y-auto px-1 pr-3">
                {/* Organization Info */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <Label>Company Name *</Label>
                      <Input
                        value={editingOrg.name}
                        onChange={(e) => setEditingOrg({ ...editingOrg, name: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Shortname (Slug) *</Label>
                      <Input value={editingOrg.slug} className="font-mono mt-2 bg-gray-50" disabled />
                      <p className="text-xs text-gray-500 mt-1">Cannot be changed</p>
                    </div>
                  </div>
                  {editingOrg.dbName && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                      <Database className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-green-700 font-medium">Database</p>
                        <p className="font-mono text-sm text-green-800 font-bold">{editingOrg.dbName}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Owner Details */}
                <div className="pt-4 border-t border-gray-200 space-y-4">
                  <h3 className="font-semibold text-sm flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-600" />
                    Owner Details
                  </h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <Label>Owner Name *</Label>
                      <Input
                        value={editingOrg.ownerName}
                        onChange={(e) => setEditingOrg({ ...editingOrg, ownerName: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Owner Email *</Label>
                      <Input
                        type="email"
                        value={editingOrg.ownerEmail}
                        onChange={(e) => setEditingOrg({ ...editingOrg, ownerEmail: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Owner Phone</Label>
                    <Input
                      value={editingOrg.ownerPhone || ''}
                      onChange={(e) => setEditingOrg({ ...editingOrg, ownerPhone: e.target.value })}
                      placeholder="+1-555-1234"
                      className="mt-2"
                    />
                  </div>
                </div>

                {/* Admin Login */}
                {adminUser && (
                  <div className="pt-4 border-t border-gray-200 space-y-4">
                    <h3 className="font-semibold text-sm flex items-center gap-2">
                      <Lock className="h-4 w-4 text-gray-600" />
                      Admin Login
                    </h3>
                    <div>
                      <Label>Admin Username *</Label>
                      <Input
                        value={adminUser.username}
                        onChange={(e) => setAdminUser({ ...adminUser, username: e.target.value })}
                        className="font-mono mt-2"
                      />
                    </div>
                    <div>
                      <Label>Reset Password (Optional)</Label>
                      <div className="relative mt-2">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={newAdminPassword}
                          onChange={(e) => setNewAdminPassword(e.target.value)}
                          placeholder="Leave blank to keep current"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* GetMyBoat integration (white-glove, per-org) */}
                <GmbIntegrationCard org={editingOrg} requirePin={requirePin} />

                {/* Action Buttons */}
                <div className="flex justify-between pt-6 border-t border-gray-200">
                  <div>
                    {editingOrg.status === 'active' && (
                      <Button
                        variant="outline"
                        onClick={() => handleLoginAsOrg(editingOrg)}
                        className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                        data-testid="login-as-org-button"
                      >
                        <User className="h-4 w-4 mr-2" />
                        Login as
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700" data-testid="org-save-changes-button">
                      Save Changes
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmDialog} onOpenChange={setDeleteConfirmDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Confirm Deletion
              </DialogTitle>
            </DialogHeader>
            {orgToDelete && (
              <div className="space-y-4">
                <p className="text-sm text-gray-700">{orgToDelete.confirmMessage}</p>

                {orgToDelete.newStatus === 'deleted' && (
                  <>
                    <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
                      <p className="text-xs font-semibold text-red-800 mb-2">WARNING: This will delete:</p>
                      <ul className="text-xs text-red-700 space-y-1 ml-4">
                        <li>• All team members in this organization</li>
                        <li>• All assets in this organization</li>
                        <li>• All trips and bookings</li>
                        <li>• All time entries</li>
                        <li>• All services and custom data</li>
                      </ul>
                      <p className="text-xs font-bold text-red-900 mt-2">THIS ACTION CANNOT BE UNDONE!</p>
                    </div>
                    <div>
                      <Label htmlFor="deleteConfirm" className="text-sm font-semibold text-gray-700">
                        Type &quot;Delete&quot; to confirm permanent deletion:
                      </Label>
                      <Input
                        id="deleteConfirm"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder="Delete"
                        className="mt-2"
                        data-testid="delete-confirm-input"
                      />
                    </div>
                  </>
                )}

                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDeleteConfirmDialog(false);
                      setOrgToDelete(null);
                      setDeleteConfirmText("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={confirmDelete}
                    disabled={orgToDelete.newStatus === 'deleted' && deleteConfirmText !== 'Delete'}
                    className={orgToDelete.newStatus === 'deleted'
                      ? 'bg-red-600 hover:bg-red-700 disabled:opacity-50'
                      : 'bg-orange-600 hover:bg-orange-700'}
                    data-testid="confirm-delete-button"
                  >
                    {orgToDelete.newStatus === 'deleted' ? 'Permanently Delete' : 'Soft Delete'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Sensitive-action PIN confirmation */}
        <PinModal prompt={pinPrompt} onClose={() => setPinPrompt(null)} />
      </div>
    </TabPageLayout>
  );
};

export default OrganizationsManagement;
